import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Button, ButtonProps } from "@/atoms-react/button/Button.react";
import { Modal } from "@/atoms-react/modal/index.react";
import { Base, SectionTitle } from "@/atoms-react/text/Text.react";
import { asSubstep } from "@/features/flow/types";
import { getPreviousSubstep } from "@/features/register/utils";
import { Routes } from "@/features/router";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setIsLaymoonFlowGoingBackFromPasswordStep } from "@/redux/slices/signupSlice";
import { Themes } from "@/types/theme";
import { getRoute, OlkyPassRoutes } from "@/utils/routes";
import { animatedNext } from "./layout/AnimatedForm.react";

export const FlowHeader = ({
  title,
  description,
  sub_description,
  back,
  goHomePage,
  onPreviousClick
}: {
  title: string;
  description?: string | ReactNode;
  sub_description?: string | ReactNode;
  back?: boolean;
  goHomePage?: boolean;
  onPreviousClick?: () => void;
}) => (
  <div>
    {(back || back === undefined) && !goHomePage && (
      <BackButton onPreviousClick={onPreviousClick} />
    )}
    {goHomePage !== undefined && goHomePage && <HomeButton />}
    <SectionTitle className={"whitespace-pre-line pb-4 font-bold"}>{title}</SectionTitle>
    {description && <Base className={sub_description ? "pb-2" : "pb-8"}>{description}</Base>}
    {sub_description && <Base className={"pb-8"}>{sub_description}</Base>}
  </div>
);

export type BackButtonProps = {
  onClick?: (event: React.MouseEvent) => void;
  onPreviousClick?: () => void;
} & Omit<ButtonProps, "onClick">;

export const BackButton = ({ className, onClick, onPreviousClick, ...props }: BackButtonProps) => {
  const substepsOfMissingfields = useAppSelector((state) => state.missingFields.substeps);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["pass"]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { session_uuid, block, step, substep } = useParams();
  const dispatch = useAppDispatch();
  const isLaymoonFlow = searchParams.get("mirror_uuid") !== null;
  const isLaymoonFlowInVerifyEMailStep = isLaymoonFlow && step === "verify_email";

  const handleClick = () => {
    if (onPreviousClick) onPreviousClick();
    if (step == "phone") {
      setIsOpen(true);
    } else
      animatedNext("back", async () => {
        if (step === "identity") {
          const previousSubstep = getPreviousSubstep(
            block ?? "",
            substepsOfMissingfields,
            asSubstep(substep)
          );
          navigate(
            getRoute(Routes.flow_substep, {
              session_uuid,
              block,
              step,
              substep: previousSubstep
            })
          );
        } else if (step === "security" && isLaymoonFlow) {
          dispatch(setIsLaymoonFlowGoingBackFromPasswordStep(true));
          navigate(
            getRoute(Routes.flow_substep, {
              session_uuid,
              block,
              step: "identity"
              //we can't go directly to source_income substep because DeclarativeIdentityRoot
              //calculates+redirects to the substep
            })
          );
        } else {
          if (!window.history || window.history?.length === 0) {
            navigate(Routes.flow + "/-/welcome");
          }
          //check if previous page is external
          if (window.history.state?.idx === 0) {
            navigate(Routes.flow + "/-/welcome");
            return;
          }

          const currentUrl = window.location.pathname;
          navigate(-1);
          const newCurrentUrl = window.location.pathname;
          //sometimes navigate(-1) stays on the same page -> we need to redirect again
          //currentUrl is equal to newCurrentUrl also in: register flow, password step
          //it's why we need also to check if it's in verify_email to isolate this case
          if (currentUrl === newCurrentUrl && newCurrentUrl === "/flow/-/signup/verify_email") {
            navigate("/", { replace: true });
            return;
          }
        }
      });
  };

  return (
    <>
      {isLaymoonFlowInVerifyEMailStep ? (
        <></>
      ) : (
        <Button
          theme={Themes.iconButton}
          icon={<ArrowLeftIcon />}
          size="lg"
          className={twMerge("mb-2 xl:mb-4", className)}
          onClick={(e) => (onClick ? onClick(e) : handleClick())}
          {...props}
        />
      )}

      <Modal
        className="w-full min-w-xl max-w-xl items-center justify-center sm:w-full"
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <div className="flex flex-col items-center justify-center p-1">
          <Base className="items-center text-center font-bold text-xl">
            {t("flow.id_control.signup.label.question")}
          </Base>

          <Base className="mt-6 items-center text-center font-semibold text-base text-slate-400">
            {t("flow.id_control.signup.label.question_description")}
          </Base>

          <Button size="lg" className="mt-10" theme={Themes.primary} onClick={() => navigate(0)}>
            {t("flow.id_control.signup.label.question_response1")}
          </Button>
          <Button
            size="lg"
            className="mt-4 text-primary"
            theme={Themes.white}
            onClick={() => setIsOpen(false)}
          >
            {t("flow.id_control.signup.label.question_response_2")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export const HomeButton = ({ className, onClick, ...props }: BackButtonProps) => {
  const navigate = useNavigate();
  const handleClick = () => animatedNext("back", async () => navigate(OlkyPassRoutes.pass));

  return (
    <Button
      theme={Themes.iconButton}
      icon={<ArrowLeftIcon />}
      size="lg"
      className={twMerge("mb-4", className)}
      onClick={(e) => (onClick ? onClick(e) : handleClick())}
      {...props}
    />
  );
};
